import {
  metersToFeet,
  getFormattedNumber,
  getFormattedNumberWithPrecision,
  vdvPicofaradsUnitConversion,
  vdvMetersToFeet
} from "../common/utils";
export default class WiremapGenerator {
  chartMeasurementUnit = "meters";

  static generateTexts(data, fullWidth, marginHor, lineGap, lineWidth, pinOffset = 0) {
    let textsData = [];
    textsData.push({
      text: this.translate("wiremap_mh"),
      x: 0,
      y: lineGap - lineGap / 4,
      fontSize: lineWidth * 1.2,
      textAnchor: "start"
    });
    textsData.push({
      text: this.translate("wiremap_rh"),
      x: fullWidth,
      y: lineGap - lineGap / 4,
      fontSize: lineWidth * 1.2,
      textAnchor: "end"
    });
    data.forEach((d, i) => {
      const pinIndex = i + pinOffset;
      textsData.push({
        text: data[i],
        x: 0,
        y: (pinIndex + 1) * lineWidth + (pinIndex + 1) * lineGap + lineWidth / 2,
        fontSize: parseInt(lineWidth * 1.5, 10),
        textAnchor: "start"
      });
      textsData.push({
        text: data[i],
        x: fullWidth,
        y: (pinIndex + 1) * lineWidth + (pinIndex + 1) * lineGap + lineWidth / 2,
        fontSize: parseInt(lineWidth * 1.5, 10),
        textAnchor: "end"
      });
    });
    return textsData;
  }

  static generateVoltageTexts(data, fullWidth, lineGap, lineWidth, pinOffset = 0) {
    let textsData = [];
    const center = fullWidth / 2;
    data.forEach((d, i) => {
      const pinIndex = i + pinOffset;
      textsData.push({
        text: data[i],
        x: center - lineGap,
        y: (pinIndex + 1) * lineWidth + (pinIndex + 1) * lineGap + lineWidth / 2,
        fontSize: parseInt(lineWidth * 1.5, 10),
        textAnchor: "end"
      });
    });
    return textsData;
  }

  static generateHeadTail(
    data,
    fullWidth,
    marginHor,
    width,
    lineGap,
    lineWidth,
    pinOffset = 0
  ) {
    let lines = data.Details;
    let headLinesData = [];
    let tailLinesData = [];

    for (let i in lines) {
      let curline = lines[i];
      let pin = curline.DHPin;

      if (curline.DHPin === "S") {
        pin = 9;
      } else {
        pin = parseInt(pin, 10);
      }

      const pinIndex = pin + pinOffset;
      headLinesData.push({
        dash: false,
        pin: pin,
        line: [
          {
            x: 0 + marginHor,
            y: lineGap * pinIndex + lineWidth * pinIndex
          },
          {
            x: width + marginHor,
            y: lineGap * pinIndex + lineWidth * pinIndex
          }
        ]
      });

      tailLinesData.push({
        dash: false,
        pin: pin,
        line: [
          {
            x: fullWidth - marginHor - width,
            y: lineGap * pinIndex + lineWidth * pinIndex
          },
          {
            x: fullWidth - marginHor,
            y: lineGap * pinIndex + lineWidth * pinIndex
          }
        ]
      });
    }
    return {
      headlines: headLinesData,
      taillines: tailLinesData
    };
  }

  static generateVoltageHeadTail(
    data,
    voltages,
    fullWidth,
    width,
    lineGap,
    lineWidth,
    pinOffset = 0
  ) {
    const center = fullWidth / 2;
    let lines = data.Details;
    let headLinesData = [];
    let voltageTexts = [];

    for (let i in lines) {
      let curline = lines[i];
      let pin = curline.DHPin;

      if (curline.DHPin === "S") {
        pin = 9;
      } else {
        pin = parseInt(pin, 10);
      }

      const pinIndex = pin + pinOffset;
      headLinesData.push({
        dash: false,
        pin: pin,
        line: [
          {
            x: center - (lineGap / 2),
            y: lineGap * pinIndex + lineWidth * pinIndex
          },
          {
            x: width + center - (lineGap / 2),
            y: lineGap * pinIndex + lineWidth * pinIndex
          }
        ]
      });

      if (voltages[pin]){
        const voltageValue = parseInt(voltages[pin]);

        if (voltageValue !== 0){
          const voltageText = voltageValue > 0 ? `+${voltageValue}V` : `${voltageValue}V`;
          voltageTexts.push({
            text: voltageText,
            x: center + lineGap,
            y: (lineGap * pinIndex + lineWidth * pinIndex) + lineWidth / 2,
            fontSize: parseInt(lineWidth * 1.5, 10),
            textAnchor: "start"
          })
        }
      }

    }
    return {
      headlines: headLinesData,
      voltagetexts: voltageTexts
    };
  }

  static generateLines(
    data,
    fullWidth,
    marginHor,
    lineGap,
    lineWidth,
    isCoax = false,
    pinOffset = 0
  ) {
    let lines = data.Details;
    let wireLength = data.RefLength;
    let totalwidth = fullWidth - marginHor * 2;

    let linesData = [];
    let shortLinesData = [];
    let openLinesData = [];
    let gridSize = totalwidth / 12;
    for (let i in lines) {
      let curline = lines[i];
      let pin = curline.DHPin;

      if (curline.DHPin === "S") {
        pin = 9;
      } else {
        pin = parseInt(pin, 10);
      }

      //dash line or solid line
      let isDashed = false;
      if (!isCoax && pin !== 9 && pin % 2) {
        isDashed = true;
      }

      const pinIndex = pin + pinOffset;

      //If Split
      if (curline.IsUndefined === false && curline.IsSplit === true) {
        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: 0 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 2 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
        linesData.push({
          dash: false,
          pin: pin,
          lineColor: "red",
          line: [
            {
              x: gridSize * 2 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 10 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: gridSize * 10 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 12 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
      }
      //FOR STRAIGHT
      else if (curline.IsUndefined === false && curline.IsThru === true) {
        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: 0 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 12 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
      }
      //FOR CROSS
      else if (
        curline.IsUndefined === false &&
        (curline.IsCross === true || curline.IsReverse === true) &&
        curline.CrossPin !== null
      ) {
        const crossPinIndex = pinOffset + parseInt(curline.CrossPin, 10);
        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: 0 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 3 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 9 + marginHor,
              y:
                lineGap * crossPinIndex +
                lineWidth * crossPinIndex
            },
            {
              x: gridSize * 12 + marginHor,
              y:
                lineGap * crossPinIndex +
                lineWidth * crossPinIndex
            }
          ]
        });
      }
      //FOR OPEN
      else if (curline.IsUndefined === false && curline.IsOpen === true) {
        let distanceOpen = curline.DistanceToOpen;

        let openPositionRatioInLength = distanceOpen / wireLength;
        let openPositionInWidth = totalwidth * openPositionRatioInLength;

        let mHSide = openPositionInWidth - gridSize / 2;
        if (mHSide < 0) {
          mHSide = openPositionInWidth;
        }

        let rHSide = openPositionInWidth + gridSize / 2;
        if (rHSide > totalwidth) {
          rHSide = openPositionInWidth;
        }

        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: 0 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: mHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
        linesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: rHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 12 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
        openLinesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: mHSide + marginHor - lineWidth / 2,
              y: lineGap * pinIndex + lineWidth * pinIndex - lineWidth
            },
            {
              x: mHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex - lineWidth / 2
            },
            {
              x: mHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex + lineWidth / 2
            },
            {
              x: mHSide + marginHor - lineWidth / 2,
              y: lineGap * pinIndex + lineWidth * pinIndex + lineWidth
            }
          ]
        });
        openLinesData.push({
          dash: isDashed,
          pin: pin,
          line: [
            {
              x: rHSide + marginHor + lineWidth / 2,
              y: lineGap * pinIndex + lineWidth * pinIndex - lineWidth
            },
            {
              x: rHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex - lineWidth / 2
            },
            {
              x: rHSide + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex + lineWidth / 2
            },
            {
              x: rHSide + marginHor + lineWidth / 2,
              y: lineGap * pinIndex + lineWidth * pinIndex + lineWidth
            }
          ]
        });
      } else if (
        curline.IsUndefined === true &&
        curline.IsThru === true &&
        pin === 9
      ) {
        //Special Case for Shield Line
        linesData.push({
          dash: false,
          pin: pin,
          //"lineWidth": lineWidth / 4,
          //"lineColor": "red",
          line: [
            {
              x: 0 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            },
            {
              x: gridSize * 12 + marginHor,
              y: lineGap * pinIndex + lineWidth * pinIndex
            }
          ]
        });
      }

      //Short
      if (curline.IsShort) {
        let shortpins = curline.ShortPins;
        if (shortpins) {
          for (let i in shortpins) {
            let targetPin = shortpins[i].Pin;

            if (targetPin === "S") {
              targetPin = 9;
            } else {
              targetPin = parseInt(targetPin, 10);
            }
            const targetPinIndex = targetPin + pinOffset;

            let shortDH = shortpins[i].ShortDH;
            if (shortDH === null) {
              shortDH = wireLength - shortpins[i].ShortRH;
            }
            let shortPositionRatioInLength = (shortDH / wireLength) || 0;
            let shortPositionInWidth =
              (fullWidth - marginHor * 2) * shortPositionRatioInLength;
            shortLinesData.push({
              marker: true,
              line: [
                {
                  x: shortPositionInWidth + marginHor,
                  y: lineGap * pinIndex + lineWidth * pinIndex
                },
                {
                  x: shortPositionInWidth + marginHor,
                  y: lineGap * targetPinIndex + lineWidth * targetPinIndex
                }
              ]
            });
          }
        }
      }
    }

    return {
      lines: linesData,
      shortlines: shortLinesData,
      openlines: openLinesData
    };
  }

  static generateTableData(data, isCoax, hideLength, isMobileTest) {
    let lines = data.Details;
    let wireLength = data.RefLength;

    lines.sort((a, b) => a.DHPin - b.DHPin);

    let linesData = [];

    for (let i in lines) {
      let curline = lines[i];
      if (
        curline.IsUndefined === false &&
        (curline.IsReverse ||
          curline.IsCross ||
          curline.IsOpen ||
          curline.IsShort ||
          curline.IsSplit)
      ) {
        let curlineData = {
          main: curline.DHPin,
          faults: []
        };

        if (curline.IsSplit) {
          curlineData.faults.push(this.translate("wiremap_split"));
        }
        if (curline.IsCross || curline.IsReverse) {
          if (curline.CrossPin) {
            curlineData.remote = curline.CrossPin;
            curlineData.faults.push(
              curline.IsCross
                ? this.translate("wiremap_cross")
                : this.translate("wiremap_reverse")
            );
          }
        }
        if (curline.IsOpen) {
          let distanceToFaultText = (hideLength) ? "" :
                                    " @ " +
                                    (curline.DistanceToOpen
                                      ? this.convertLength(curline.DistanceToOpen, isMobileTest)
                                      : this.convertLength(0, isMobileTest));

          curlineData.faults.push(
            this.translate("wiremap_open") + distanceToFaultText
          );
        }
        if (curline.IsShort) {
          let shortpins = curline.ShortPins;
          if (shortpins) {
            for (let j in shortpins) {
              if (shortpins[j].Pin === "S" || curline.DHPin === "S") {
                curlineData.faults.push(
                  shortpins[j].Pin + " " + this.translate("wiremap_short")
                );
              } else if (shortpins[j].ShortDH === 0) {
                let distanceToFaultText = (hideLength) ? "" : " " + this.convertLength(shortpins[j].ShortDH, isMobileTest)
                curlineData.faults.push(
                  shortpins[j].Pin +
                    " " +
                    this.translate("wiremap_short") +
                    " @ " +
                    this.translate("wiremap_mh") +
                    distanceToFaultText
                );
              } else if (shortpins[j].ShortRH === 0) {
                let distanceToFaultText = (hideLength) ? "" : " " + this.convertLength(shortpins[j].ShortRH, isMobileTest)
                curlineData.faults.push(
                  shortpins[j].Pin +
                    " " +
                    this.translate("wiremap_short") +
                    " @ " +
                    this.translate("wiremap_rh") +
                    distanceToFaultText
                );
              } else {
                let shortDH =
                  shortpins[j].ShortDH !== null
                    ? shortpins[j].ShortDH
                    : wireLength - shortpins[j].ShortRH;
                let shortRH =
                  shortpins[j].ShortRH !== null
                    ? shortpins[j].ShortRH
                    : wireLength - shortpins[j].ShortDH;
                if (shortDH <= shortRH || shortpins[j].ShortRH == null) {
                  if (isCoax){
                    let distanceToFaultText = (hideLength) ? "" : " " + this.convertLength(shortDH, isMobileTest);
                    const pinName = shortpins[j].Pin === '1' || shortpins[j].Pin === '4'  ? "C" : "S";
                    curlineData.faults.push(
                      pinName +
                      " " +
                      this.translate("wiremap_short") +
                      " @ " +
                      this.translate("wiremap_mh") +
                      distanceToFaultText
                    );
                  } else {
                    let distanceToFaultText = (hideLength) ? "" : " " + this.convertLength(shortDH, isMobileTest);
                    curlineData.faults.push(
                      shortpins[j].Pin +
                      " " +
                      this.translate("wiremap_short") +
                      " @ " +
                      this.translate("wiremap_mh") +
                      distanceToFaultText
                    );
                  }
                } else {
                  let distanceToFaultText = (hideLength) ? "" :  " " + this.convertLength(shortRH, isMobileTest)
                  curlineData.faults.push(
                    shortpins[j].Pin +
                      " " +
                      this.translate("wiremap_short") +
                      " @ " +
                      this.translate("wiremap_rh") +
                      distanceToFaultText
                  );
                }
              }
            }
          }
        }
        linesData.push(curlineData);
      }
    }

    return {
      lines: linesData,
      nodata: !linesData.length
    };
  }
  static getLineInfo(details, line) {
    if (line === 9 || line === "9") {
      line = "S";
    }
    for (let i in details) {
      if (details[i].DHPin === line.toString()) {
        return details[i];
      }
    }
  }

  static convertMeasurementUnit(val) {
    if (
      this.chartMeasurementUnit === "meters" ||
      this.chartMeasurementUnit === "meter"
    ) {
      return (
        getFormattedNumber(val) +
        " " +
        this.translate("chart_measurement_unit_meter_sort")
      );
    } else {
      return `${metersToFeet(val)}`;
    }
  }

  static convertMeasurementUnitVDV(val) {
    if (
      this.chartMeasurementUnit === "meters" ||
      this.chartMeasurementUnit === "meter"
    ) {
      return (
        getFormattedNumberWithPrecision(val, 1) +
        " " +
        this.translate("chart_measurement_unit_meter_sort")
      );
    } else {
      return `${vdvMetersToFeet(val)}`;
    }
  }

  static convertLength(val, isMobile) {
    return isMobile ? this.convertMeasurementUnitVDV(val) : this.convertMeasurementUnit(val)
  }

  static getCapacitanceFromNVP(val) {
    let capacitance = val.replace('%', '');
    if (
      this.chartMeasurementUnit === "meters" ||
      this.chartMeasurementUnit === "meter"
    ) {
      return getFormattedNumberWithPrecision(parseFloat(capacitance), 1)
    }
    return vdvPicofaradsUnitConversion(parseFloat(capacitance)).toString();
  }

  static setChartMeasurementUnit(chartMeasurementUnit) {
    this.chartMeasurementUnit = chartMeasurementUnit;
  }

  static setTranslate(translate) {
    this.translate = translate;
  }
}
